import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { content } from '../data/content';

interface DemoViewerProps {
  isOpen: boolean;
  onClose: () => void;
}

const DemoViewer: React.FC<DemoViewerProps> = ({ isOpen, onClose }) => {
  const { demoVideos } = content;
  const [activeIndex, setActiveIndex] = useState(0);
  const [isTheaterMode, setIsTheaterMode] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const theaterContainerRef = useRef<HTMLDivElement>(null);

  // Cinema-grade easing
  const cinemaEasing = [0.22, 1, 0.36, 1];

  useEffect(() => {
    if (isOpen) {
      setActiveIndex(0);
      setIsTheaterMode(false);
    }
  }, [isOpen]);

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleProgressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = parseFloat(e.target.value);
    setCurrentTime(newTime);
    if (videoRef.current) {
      videoRef.current.currentTime = newTime;
    }
  };

  const toggleFullscreen = async () => {
    const container = theaterContainerRef.current;
    if (!container) return;

    if (document.fullscreenElement) {
      await document.exitFullscreen();
    } else {
      await container.requestFullscreen();
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      
      if (e.key === 'Escape') {
        if (isTheaterMode) {
          setIsTheaterMode(false);
        } else {
          onClose();
        }
      }
      
      if (!isTheaterMode) {
        if (e.key === 'ArrowLeft') {
          goToPrevious();
        } else if (e.key === 'ArrowRight') {
          goToNext();
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isTheaterMode, activeIndex]);

  // Wheel navigation
  useEffect(() => {
    if (!isOpen || isTheaterMode) return;
    
    let wheelTimeout: NodeJS.Timeout;
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      clearTimeout(wheelTimeout);
      
      const delta = e.deltaX || e.deltaY;
      if (Math.abs(delta) > 30) {
        wheelTimeout = setTimeout(() => {
          if (delta > 0) {
            goToNext();
          } else {
            goToPrevious();
          }
        }, 50);
      }
    };
    
    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      return () => container.removeEventListener('wheel', handleWheel);
    }
  }, [isOpen, isTheaterMode, activeIndex]);

  const goToNext = () => {
    setActiveIndex((prev) => (prev + 1) % demoVideos.length);
  };

  const goToPrevious = () => {
    setActiveIndex((prev) => (prev - 1 + demoVideos.length) % demoVideos.length);
  };

  const animateToIndex = (index: number) => {
    setActiveIndex(index);
  };

  const enterTheaterMode = (index: number) => {
    setActiveIndex(index);
    setIsTheaterMode(true);
  };

  if (!demoVideos || !isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6, ease: cinemaEasing }}
      className="fixed inset-0 z-50 overflow-hidden"
    >
      {/* Deep Vignette Background */}
      <div className="absolute inset-0 bg-gradient-radial from-gray-900 via-black to-black">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,rgba(0,0,0,0.6)_100%)]" />
      </div>

      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-8 right-8 z-50 text-white/40 hover:text-white transition-colors duration-300 p-3 hover:bg-white/5 rounded-full backdrop-blur-sm"
      >
        <X size={28} strokeWidth={1.5} />
      </button>

      <AnimatePresence mode="wait">
        {!isTheaterMode ? (
          /* CAROUSEL MODE */
          <motion.div
            key="carousel"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            ref={containerRef}
            className="h-full w-full flex items-center justify-center px-4 md:px-8 lg:px-16"
          >
            <div className="relative w-full max-w-[1800px] h-[70vh] flex items-center justify-center">
              {/* Fixed Center Container - Only content rotates */}
              <div className="relative w-full h-full flex items-center justify-center">
                {demoVideos.map((video, index) => {
                  // Calculate offset with infinite loop logic
                  let offset = index - activeIndex;
                  
                  // Handle infinite loop: normalize offset to [-1, 0, 1]
                  if (offset > demoVideos.length / 2) {
                    offset -= demoVideos.length;
                  } else if (offset < -demoVideos.length / 2) {
                    offset += demoVideos.length;
                  }
                  
                  const isActive = offset === 0;
                  const absOffset = Math.abs(offset);
                  
                  // Only show cards within range [-1, 0, 1]
                  if (absOffset > 1) return null;
                  
                  return (
                    <motion.div
                      key={video.id}
                      className="absolute"
                      style={{
                        width: 'clamp(350px, 60vw, 1200px)',
                      }}
                      animate={{
                        x: `${offset * 75}%`,
                        scale: isActive ? 1 : 0.85,
                        opacity: isActive ? 1 : 0.4,
                        filter: isActive ? 'blur(0px) saturate(1)' : 'blur(8px) saturate(0.7)',
                        zIndex: isActive ? 20 : 10,
                      }}
                      transition={{
                        duration: 0.7,
                        ease: cinemaEasing,
                      }}
                      onClick={() => {
                        if (isActive) {
                          enterTheaterMode(index);
                        } else {
                          animateToIndex(index);
                        }
                      }}
                    >
                      {/* Card Container */}
                      <div
                        className="relative aspect-video rounded-3xl overflow-hidden shadow-2xl border border-white/5 group"
                        style={{
                          boxShadow: isActive
                            ? '0 30px 80px -15px rgba(0,0,0,0.8), 0 0 1px rgba(255,255,255,0.1)'
                            : '0 10px 40px -10px rgba(0,0,0,0.5)',
                        }}
                      >
                        {/* Poster Image */}
                        <img
                          src={video.thumbnail}
                          alt={video.title}
                          className="w-full h-full object-cover"
                          draggable={false}
                          loading="lazy"
                          decoding="async"
                        />

                        {/* Gradient Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                        {/* Bottom Info - Only on active card */}
                        {isActive && (
                          <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3, duration: 0.5, ease: cinemaEasing }}
                            className="absolute bottom-0 left-0 right-0 p-8"
                          >
                            {/* Frosted Glass CTA Button */}
                            <motion.button
                              whileHover={{ y: -4, scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                enterTheaterMode(index);
                              }}
                              className="px-4 py-2 md:px-6 md:py-3 rounded-full bg-white/10 backdrop-blur-xl border border-white/20 text-white text-xs md:text-sm font-medium tracking-wide hover:bg-white/20 transition-all duration-300 inline-flex items-center gap-2 group/btn"
                              style={{
                                boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                              }}
                            >
                              <span>立即观看</span>
                            </motion.button>
                          </motion.div>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              {/* Progress Dots */}
              <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2.5">
                {demoVideos.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => animateToIndex(index)}
                    className="group"
                  >
                    <div
                      className={`h-1.5 rounded-full transition-all duration-500 ${
                        index === activeIndex
                          ? 'w-12 bg-white'
                          : 'w-1.5 bg-white/30 group-hover:bg-white/50'
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        ) : (
          /* THEATER MODE */
          <motion.div
            key="theater"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="h-full w-full flex items-center justify-center p-4 md:p-12"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setIsTheaterMode(false);
              }
            }}
          >
            {/* Deep Vignette */}
            <div className="absolute inset-0 bg-black/95">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,rgba(0,0,0,0.8)_100%)]" />
            </div>

            {/* Video Player Container */}
            <motion.div
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.6, ease: cinemaEasing }}
              className="relative w-full max-w-7xl bg-black rounded-2xl shadow-2xl z-10"
              style={{
                boxShadow: '0 50px 100px -20px rgba(0,0,0,0.9)',
              }}
            >
              <div
                ref={theaterContainerRef}
                className="relative w-full aspect-video overflow-hidden rounded-2xl"
              >
                <video
                  ref={videoRef}
                  key={`theater-${activeIndex}`}
                  src={demoVideos[activeIndex].videoUrl}
                  poster={demoVideos[activeIndex].thumbnail}
                  autoPlay
                  muted={isMuted}
                  onTimeUpdate={handleTimeUpdate}
                  onLoadedMetadata={handleLoadedMetadata}
                  className="w-full h-full"
                />

                {/* Back Button */}
                <motion.button
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                  onClick={() => setIsTheaterMode(false)}
                  className="absolute top-3 left-3 sm:top-6 sm:left-6 px-3 py-1.5 sm:px-5 sm:py-2.5 rounded-full bg-black/40 backdrop-blur-xl border border-white/10 text-white/80 hover:text-white hover:bg-black/60 transition-all duration-300 flex items-center gap-2 text-xs sm:text-sm font-medium z-20"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  返回
                </motion.button>
              </div>

              {/* Video Controls */}
              <div className="relative mt-3 px-4 py-3 sm:absolute sm:bottom-0 sm:left-0 sm:right-0 sm:mt-0 sm:p-6 bg-black/70 sm:bg-gradient-to-t sm:from-black/90 sm:to-transparent opacity-100 sm:opacity-0 sm:hover:opacity-100 transition-opacity duration-300">
                {/* Progress Bar */}
                <div className="mb-3 sm:mb-4">
                  <input
                    type="range"
                    min="0"
                    max={duration || 0}
                    value={currentTime}
                    onChange={handleProgressChange}
                    className="w-full h-1 bg-white/20 rounded-full cursor-pointer appearance-none [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:border-0"
                  />
                </div>

                {/* Control Buttons */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 sm:gap-4">
                    {/* Play/Pause Button */}
                    <button
                      onClick={togglePlayPause}
                      className="p-1.5 sm:p-2 rounded-full hover:bg-white/20 transition-colors duration-300"
                    >
                      {isPlaying ? (
                        <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      )}
                    </button>

                    {/* Mute Button */}
                    <button
                      onClick={toggleMute}
                      className="p-1.5 sm:p-2 rounded-full hover:bg-white/20 transition-colors duration-300"
                    >
                      {isMuted ? (
                        <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C23.1 14.69 24 13.53 24 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 1c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
                        </svg>
                      )}
                    </button>

                    {/* Time Display */}
                    <span className="text-white/70 text-xs sm:text-sm">
                      {Math.floor(currentTime / 60)}:{String(Math.floor(currentTime % 60)).padStart(2, '0')} / {Math.floor(duration / 60)}:{String(Math.floor(duration % 60)).padStart(2, '0')}
                    </span>
                  </div>
                  <button
                    onClick={toggleFullscreen}
                    className="p-1.5 sm:p-2 rounded-full hover:bg-white/20 transition-colors duration-300"
                    aria-label="全屏"
                  >
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M7 14H5v5h5v-2H7v-3zm0-4h2V7h3V5H5v5zm10 9h-3v2h5v-5h-2v3zm0-9V7h-3V5h5v5h-2z" />
                    </svg>
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default DemoViewer;
